import { create } from 'zustand';
import type {
  Adventure,
  Instance,
  InstanceState,
  InstanceStatus,
  InitiativeRow,
} from '@gm-binder/shared';
import { api } from '../api/client';

interface RunStore {
  instanceId: string | null;
  instance: Instance | null;
  adventure: Adventure | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  load: (instanceId: string) => Promise<void>;
  reset: () => void;
  /** Replace the loaded instance (e.g. after roster/close-day mutations). */
  setInstance: (instance: Instance) => void;
  /** Re-fetch the adventure template (e.g. after a compendium import added an entity). */
  refreshAdventure: () => Promise<void>;

  /** Mutate the live session state and schedule a debounced autosave. */
  updateState: (mutator: (s: InstanceState) => void) => void;
  setStatus: (status: InstanceStatus) => Promise<void>;
  rename: (name: string) => Promise<void>;

  // Convenience helpers used across section pages.
  toggleVisited: (roomId: string) => void;
  setNotes: (notes: string) => void;
  setHp: (name: string, hp: number) => void;
  toggleActiveCharacter: (name: string) => void;
  setInitiative: (rows: InitiativeRow[]) => void;
  setInitiativeRound: (round: number) => void;
  setInitiativeActive: (index: number) => void;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSave(get: () => RunStore, set: (p: Partial<RunStore>) => void) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    const { instanceId, instance } = get();
    if (!instanceId || !instance) return;
    set({ saving: true });
    try {
      const updated = await api.patchInstance(instanceId, { state: instance.state });
      set({ instance: updated, saving: false });
    } catch (e) {
      set({ saving: false, error: e instanceof Error ? e.message : 'Save failed' });
    }
  }, 600);
}

export const useRunStore = create<RunStore>((set, get) => ({
  instanceId: null,
  instance: null,
  adventure: null,
  loading: false,
  saving: false,
  error: null,

  async load(instanceId) {
    if (get().instanceId === instanceId && get().instance && get().adventure) return;
    set({ loading: true, error: null, instanceId });
    try {
      const instance = await api.getInstance(instanceId);
      const adventure = await api.getAdventure(instance.adventureSlug);
      set({ instance, adventure, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Load failed' });
    }
  },

  reset() {
    if (saveTimer) clearTimeout(saveTimer);
    set({ instanceId: null, instance: null, adventure: null, error: null });
  },

  setInstance(instance) {
    set({ instance });
  },

  async refreshAdventure() {
    const { instance } = get();
    if (!instance) return;
    try {
      const adventure = await api.getAdventure(instance.adventureSlug);
      set({ adventure });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Refresh failed' });
    }
  },

  updateState(mutator) {
    const { instance } = get();
    if (!instance) return;
    const nextState: InstanceState = structuredClone(instance.state);
    mutator(nextState);
    set({ instance: { ...instance, state: nextState } });
    scheduleSave(get, set);
  },

  async setStatus(status) {
    const { instanceId, instance } = get();
    if (!instanceId || !instance) return;
    const updated = await api.patchInstance(instanceId, { status });
    set({ instance: updated });
  },

  async rename(name) {
    const { instanceId } = get();
    if (!instanceId) return;
    const updated = await api.patchInstance(instanceId, { name });
    set({ instance: updated });
  },

  toggleVisited(roomId) {
    get().updateState((s) => {
      const i = s.visitedRooms.indexOf(roomId);
      if (i >= 0) s.visitedRooms.splice(i, 1);
      else s.visitedRooms.push(roomId);
    });
  },

  setNotes(notes) {
    get().updateState((s) => {
      s.notes = notes;
    });
  },

  setHp(name, hp) {
    get().updateState((s) => {
      s.hp[name] = hp;
    });
  },

  toggleActiveCharacter(name) {
    get().updateState((s) => {
      const i = s.inactiveCharacters.indexOf(name);
      if (i >= 0) s.inactiveCharacters.splice(i, 1);
      else s.inactiveCharacters.push(name);
    });
  },

  setInitiative(rows) {
    get().updateState((s) => {
      s.initiative = rows;
    });
  },

  setInitiativeRound(round) {
    get().updateState((s) => {
      s.initiativeRound = round;
    });
  },

  setInitiativeActive(index) {
    get().updateState((s) => {
      s.initiativeActive = index;
    });
  },
}));
